import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import {
  HeartPulse, FileText, FileDown, Mail, Building2, Phone, MapPin,
  ArrowRight, CheckCircle2, Users, Calendar, MessageSquare, ExternalLink,
  Globe, Sparkles, Heart, Baby, UserRound, Scale, GraduationCap, Megaphone,
} from "lucide-react";

const DOC_GROUPS = [
  {
    key: "bailleurs",
    title: "Dossiers bailleurs & pitch",
    icon: Sparkles,
    docs: [
      { title: "Dossier technique & financier (FR)", desc: "Pilote 3 hôpitaux, 3 mois — 41 400 USD : budget, calendrier, résultats, suivi-évaluation", file: "/pdf/DOSSIER_TECHNIQUE_FINANCIER_PILOTE.pdf" },
      { title: "Technical & Financial Proposal (EN)", desc: "Pilot 3 hospitals, 3 months — USD 41,400: budget, timeline, results, M&E", file: "/pdf/PILOT_TECHNICAL_FINANCIAL_PROPOSAL_EN.pdf" },
      { title: "Pitch document complet (FR)", desc: "Document détaillé pour bailleurs francophones", file: "/pdf/PITCH_DOCUMENT.pdf" },
      { title: "Pitch document complet (EN)", desc: "Detailed dossier for international funders", file: "/pdf/PITCH_DOCUMENT_EN.pdf" },
      { title: "Pitch 1 page (FR)", desc: "Version condensée pour concours startup", file: "/pdf/PITCH_ONE_PAGER.pdf" },
      { title: "Pitch Deck — PowerPoint éditable", desc: "10 diapositives .pptx, à personnaliser pour vos présentations", file: "/PITCH_DECK_KWIKILA_CARE.pptx" },
      { title: "Budget pilote — Excel avec graphiques", desc: "41 400 USD / 3 mois : budget détaillé, répartition, calendrier de décaissement", file: "/BUDGET_PILOTE_KWIKILA_CARE.xlsx" },
      { title: "Dossier partenariat — Secrétariat Général à la Santé", desc: "Demande de reconnaissance institutionnelle et d'appui DHIS2/SNIS", file: "/pdf/DOSSIER_PARTENARIAT_SECRETARIAT_GENERAL_SANTE.pdf" },
    ],
  },
  {
    key: "legal",
    title: "Conventions légales",
    icon: Scale,
    docs: [
      { title: "Convention-cadre hôpital partenaire", desc: "Contrat formel : obligations, tarifs, propriété des données, résiliation", file: "/pdf/CONVENTION_CADRE_HOPITAL_PARTENAIRE.pdf" },
      { title: "Convention de traitement des données", desc: "Protection des données de santé, hébergement, droits des patients (DPA)", file: "/pdf/CONVENTION_TRAITEMENT_DONNEES.pdf" },
      { title: "Lettre d'intention hôpital", desc: "Modèle à faire signer par un directeur d'hôpital pilote", file: "/pdf/LETTRE_INTENTION_TEMPLATE.pdf" },
    ],
  },
  {
    key: "guides",
    title: "Guides & déploiement",
    icon: GraduationCap,
    docs: [
      { title: "Guide de déploiement local RDC", desc: "Docker + MinIO : héberger la plateforme sur un datacenter congolais", file: "/pdf/DEPLOIEMENT_LOCAL_RDC.pdf" },
      { title: "Guide de formation & droits d'accès", desc: "Guide multilingue par rôle, pour le personnel hospitalier", file: "/pdf/GUIDE_FORMATION_DROITS_ACCES.pdf" },
      { title: "Scripts vidéos de formation", desc: "Scénarios prêts à tourner pour chaque rôle utilisateur", file: "/pdf/SCRIPTS_VIDEOS_FORMATION.pdf" },
    ],
  },
  {
    key: "prospection",
    title: "Prospection & communication",
    icon: Megaphone,
    docs: [
      { title: "Contacts hôpitaux Kinshasa", desc: "Top 6 hôpitaux + méthode de prospection", file: "/pdf/CONTACTS_HOPITAUX.pdf" },
      { title: "5 emails bailleurs prêts à envoyer", desc: "Gates, USAID, AFD, GIZ, Banque Mondiale", file: "/pdf/EMAILS_BAILLEURS.pdf" },
    ],
  },
];

const TOTAL_DOCS = DOC_GROUPS.reduce((n, g) => n + g.docs.length, 0);

export default function PressKit() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public-stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FDFBF7] text-[#1A1D1A] min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-[#E5EBE7] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-2">
          <Link to="/" className="flex items-center gap-2" data-testid="press-logo">
            <div className="w-9 h-9 rounded-xl bg-[#1E4631] grid place-items-center">
              <HeartPulse className="w-5 h-5 text-white"/>
            </div>
            <span className="font-[Outfit] font-bold text-lg">KWIKILA Care</span>
            <span className="text-xs text-[#5C6B61] hidden sm:inline">· Espace Presse & Investisseurs</span>
          </Link>
          <div className="flex gap-2">
            <Link to="/pitch" data-testid="press-to-pitch">
              <Button variant="outline" size="sm" className="border-[#1E4631] text-[#1E4631] text-xs sm:text-sm px-3 sm:px-4">Pitch deck</Button>
            </Link>
            <Link to="/dossier-subventions" data-testid="press-to-grants">
              <Button size="sm" className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white text-xs sm:text-sm px-3 sm:px-4">Tous les dossiers</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E4631]/5 text-[#1E4631] text-xs font-bold uppercase tracking-widest mb-6">
          <Sparkles className="w-3 h-3"/> Espace Presse & Investisseurs
        </div>
        <h1 className="text-5xl md:text-6xl font-[Outfit] font-bold leading-[1.05] max-w-4xl">
          Tout KWIKILA Care, <span className="brand-gradient-text">en un seul endroit</span>
        </h1>
        <p className="mt-6 text-lg text-[#5C6B61] max-w-2xl">
          Plateforme numérique de gestion patient pour les hôpitaux de la République Démocratique du Congo.
          Dossiers, statistiques, contacts — tout est public et téléchargeable.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="mailto:contact@kwikilacare.com" data-testid="press-email">
            <Button className="bg-[#1E4631] hover:bg-[#163424] text-white px-6 py-5 font-semibold">
              <Mail className="w-4 h-4 mr-2"/> Contacter l'équipe
            </Button>
          </a>
          <Link to="/dossier-subventions" data-testid="press-docs-btn">
            <Button variant="outline" className="border-[#1E4631] text-[#1E4631] px-6 py-5 font-semibold">
              <FileDown className="w-4 h-4 mr-2"/> Télécharger les {TOTAL_DOCS} documents
            </Button>
          </Link>
        </div>
      </section>

      {/* Stats live */}
      <section className="bg-white border-y border-[#E5EBE7] py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">Plateforme en temps réel</div>
          <h2 className="text-2xl md:text-3xl font-[Outfit] font-bold mb-8">État actuel du déploiement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { l: "Hôpitaux inscrits", v: stats?.hospitals?.total ?? "—", i: Building2, c: "#1E4631" },
              { l: "Patients suivis", v: stats?.patients?.total ?? "—", i: Users, c: "#D96C4A" },
              { l: "RDV programmés", v: stats?.appointments?.total ?? "—", i: Calendar, c: "#2E7D32" },
              { l: "SMS délivrés", v: stats?.sms_sent ?? "—", i: MessageSquare, c: "#E3A72F" },
            ].map((s, i) => (
              <div key={i} data-testid={`press-stat-${i}`} className="p-5 rounded-xl bg-[#FDFBF7] border border-[#E5EBE7]">
                <s.i className="w-5 h-5" style={{ color: s.c }}/>
                <div className="mt-3 text-3xl font-[Outfit] font-bold">{s.v}</div>
                <div className="text-xs text-[#5C6B61] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
          {stats && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { l: "Femmes enceintes", v: stats?.patients?.pregnant ?? 0, i: Heart },
                { l: "Enfants 0-3 ans", v: stats?.patients?.child ?? 0, i: Baby },
                { l: "Troisième âge", v: stats?.patients?.elderly ?? 0, i: UserRound },
                { l: "Patients généraux", v: stats?.patients?.general ?? 0, i: Users },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#E5EBE7]">
                  <s.i className="w-4 h-4 text-[#5C6B61]"/>
                  <div>
                    <div className="font-[Outfit] font-bold">{s.v}</div>
                    <div className="text-[10px] uppercase text-[#5C6B61] tracking-wide">{s.l}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tous les documents, par catégorie */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">{TOTAL_DOCS} documents officiels</div>
        <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold mb-3">Téléchargez tout, en 1 clic</h2>
        <p className="text-[#5C6B61] mb-10 max-w-2xl">PDF mis en page, budget Excel avec graphiques, pitch deck PowerPoint éditable. Aucun login requis.</p>
        <div className="space-y-12">
          {DOC_GROUPS.map((group) => (
            <div key={group.key} data-testid={`press-group-${group.key}`}>
              <div className="flex items-center gap-2 mb-4">
                <group.icon className="w-5 h-5 text-[#1E4631]"/>
                <h3 className="font-[Outfit] font-bold text-lg">{group.title}</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.docs.map((d) => {
                  const isBinary = d.file.endsWith(".xlsx") || d.file.endsWith(".pptx");
                  return (
                    <a key={d.file} href={d.file}
                       target={isBinary ? undefined : "_blank"} rel="noopener noreferrer"
                       download={isBinary ? d.file.split("/").pop() : undefined}
                       data-testid={`press-doc-${d.file.split("/").pop()}`}
                       className="group p-5 rounded-xl bg-white border border-[#E5EBE7] hover:border-[#1E4631] hover:shadow-lg transition">
                      <div className="flex items-start justify-between">
                        <FileText className="w-6 h-6 text-[#1E4631]"/>
                        <ExternalLink className="w-4 h-4 text-[#5C6B61] group-hover:text-[#1E4631] transition"/>
                      </div>
                      <h3 className="mt-3 font-[Outfit] font-semibold text-base">{d.title}</h3>
                      <p className="text-sm text-[#5C6B61] mt-1">{d.desc}</p>
                      <div className="mt-4 inline-flex items-center text-xs font-medium text-[#1E4631] group-hover:text-[#D96C4A] transition">
                        Télécharger {d.file.split(".").pop().toUpperCase()} <ArrowRight className="w-3 h-3 ml-1"/>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi nous soutenir */}
      <section className="bg-[#1E4631] text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#D96C4A] mb-4">Pourquoi nous soutenir</div>
          <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold mb-10">5 raisons de devenir partenaire de KWIKILA Care</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              ["Plateforme déjà fonctionnelle", "MVP testé 67/67 ✅ — pas un projet sur papier, un produit livré."],
              ["5 langues nationales RDC", "Français, Lingala, Swahili, Tshiluba, Kikongo — angle mort des solutions étrangères."],
              ["Équipe congolaise ancrée", "Fondée en RDC, par et pour la RDC. Connaissance fine du terrain."],
              ["Modèle économique pérenne", "SaaS B2B autofinanceable progressivement après pilote."],
              ["Alignement ODD ONU", "Objectifs 3, 5, 9 et 17 directement couverts. Idéal pour bailleurs internationaux."],
              ["Impact mesurable", "-40% RDV manqués · 1M patients en An 3 · KPIs précis dans les dossiers."],
            ].map(([title, desc], i) => (
              <div key={i} data-testid={`reason-${i}`} className="p-6 rounded-xl bg-white/5 backdrop-blur border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-[#D96C4A]"/>
                <h3 className="mt-3 font-[Outfit] font-semibold text-lg">{title}</h3>
                <p className="text-sm text-white/70 mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#D96C4A] to-[#B85A3D] p-10 md:p-14 text-white">
          <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold">Construisons ensemble la santé numérique en RDC</h2>
          <p className="mt-4 text-white/90 max-w-xl">Bailleurs, hôpitaux, ONG, ministères, journalistes, investisseurs : nous sommes à votre écoute.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
            <a href="mailto:contact@kwikilacare.com" className="inline-flex items-center gap-2 hover:underline"><Mail className="w-4 h-4"/> contact@kwikilacare.com</a>
            <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4"/> +243 824 314 459</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4"/> Kinshasa, RDC</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pitch" className="px-5 py-3 rounded-lg bg-white text-[#D96C4A] font-semibold text-sm hover:bg-white/90 transition inline-flex items-center" data-testid="press-cta-pitch">
              Voir le pitch en ligne <ArrowRight className="w-4 h-4 ml-1"/>
            </Link>
            <Link to="/register-hospital" className="px-5 py-3 rounded-lg bg-white/10 backdrop-blur text-white font-semibold text-sm hover:bg-white/20 transition border border-white/30 inline-flex items-center" data-testid="press-cta-register">
              Inscrire mon hôpital
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5EBE7] py-8 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-[#5C6B61]">
          © 2026 KWIKILA Care · Conçu pour la République Démocratique du Congo
          <span className="mx-2">·</span>
          <Link to="/" className="hover:text-[#1E4631]" data-testid="press-footer-home">Accueil</Link>
          <span className="mx-2">·</span>
          <Link to="/pitch" className="hover:text-[#1E4631]" data-testid="press-footer-pitch">Pitch</Link>
          <span className="mx-2">·</span>
          <Link to="/dossier-subventions" className="hover:text-[#1E4631]" data-testid="press-footer-grants">Dossiers</Link>
        </div>
      </footer>
    </div>
  );
}
